import React, { useMemo, useEffect, useState } from 'react';
import Config from 'config';
import { gql, useQuery } from '@apollo/client';
import { GetCloudConfig, GetCloudConfig_orderingCloudConfiguration } from './__queries__';
import styled from 'styled-components';
import { datadogLogs } from '@datadog/browser-logs';

// Stylish red error box
const Error = styled.div`
  margin: 80px;
  font-size: 24px;
  color: #333;
  text-align: center;
  background-color: #99002233;
  border-radius: 4px;
  padding: 20px;
`;

const Context = React.createContext(Config);

const GET_CLOUD_CONFIG = gql`
  query GetCloudConfig($cloudConfigId: String, $slug: String) {
    orderingCloudConfiguration(id: $cloudConfigId, slug: $slug) {
      id
      marketplaceId
      userCompanyId
      marketplace {
        id
        venues {
          id
          name
        }
      }
      logoUrl
      theme
      landingPageBackgroundUrl
      landingPageDesktopBackgroundUrl
      enableAnonymousOrders
      enableInvoicing
      enableTipping
      disableComments
      disablePickuptime
      serviceTypes
      greetingText
      navigationInfoText
      showCategoriesInGridView
      landingPageIntro
      landingpageSections
      aboutUs
      force3DSecure
      collectCustomerContactData
    }
  }
`;

function mapCloudConfigToLocalConfig(
  cloudConfig: GetCloudConfig_orderingCloudConfiguration,
): Partial<typeof Config> {
  return {
    COMPANY_ID: cloudConfig.userCompanyId,
    LOGO_URL: cloudConfig.logoUrl,
    MARKETPLACE_ID: cloudConfig.marketplaceId,
    MARKETPLACE_VENUES: cloudConfig.marketplace?.venues || [],
    THEME: cloudConfig.theme,
    LANDING_BACKGROUND_URL: cloudConfig.landingPageBackgroundUrl || null,
    LANDING_DESKTOP_BACKGROUND_URL: cloudConfig.landingPageDesktopBackgroundUrl || null,
    ANONYMOUS_PURCHASE: cloudConfig.enableAnonymousOrders,
    USE_INVOICE_CUSTOMERS: cloudConfig.enableInvoicing,
    DISABLE_COMMENTS: cloudConfig.disableComments,
    ENABLE_TIPS: cloudConfig.enableTipping,
    DISABLE_PICKUP_TIME: cloudConfig.disablePickuptime,
    ALLOWED_SERVICE_TYPES: cloudConfig.serviceTypes,
    GREETING: cloudConfig.greetingText,
    NAVIGATION_INFOTEXT: cloudConfig.navigationInfoText,
    MENU_CATEGORIES_GRID: cloudConfig.showCategoriesInGridView,
    FORCE_3DS: Boolean(cloudConfig.force3DSecure),
    ENABLE_PROMOCODES: true,
    SYNC_ETA: true,
    IS_CLOUD_CONFIG: true,
    LANDING_PAGE_INTRO: cloudConfig.landingPageIntro,
    LANDING_PAGE_SECTIONS: cloudConfig.landingpageSections,
    ABOUT_US: cloudConfig.aboutUs,
    COLLECT_CUSTOMER_CONTACT_DATA: cloudConfig.collectCustomerContactData,
    usesTestingConfig: false, // This is set to true if we receive a message from the iframe to test different configs
    testingVenueImages: {}, // This will be populated with images from the Quickoffice testing
  };
}

const ConfigProvider: React.FC = ({ children }) => {
  // This is used to test different configs in Quickoffice before saving and instantly applying them.
  const [testingConfig, setTestingConfig] = useState<any>({});
  const [testingVenueImages, setTestingVenueImages] = useState<{ [key: string]: string }>({});

  /**
   * Deployments can be hosted at either <brand>.shop.baemingo.se or as
   * paths on the main domain like this: shop.baemingo.se/<brand>.
   *
   * If we are hosted as a path, get the brand from the path.
   *
   * It can also be available as the "s" query parameter.
   */

  const slug = useMemo(() => {
    let brand: string | null = null;
    const path = window.location.pathname;
    if (path.startsWith('/')) {
      const parts = path.split('/');
      if (parts.length > 1) {
        brand = parts[1];
      }
    }

    const search = window.location.search;
    if (search.startsWith('?')) {
      const params = new URLSearchParams(search);
      brand = params.get('s') || brand;
    }

    return brand;
  }, [window.location.pathname, window.location.search]);

  // Listen to messages from the iframe to update the theme dynamically.
  // This is for testing themes in Quickoffice.
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data.type === 'SET_LOGO_URL' && event.data) {
        setTestingConfig((prev: any) => ({
          ...prev,
          LOGO_URL: event.data.SET_LOGO_URL,
          usesTestingConfig: true,
        }));
      }

      if (event.data.type === 'SET_LANDING_BACKGROUND_URL' && event.data) {
        setTestingConfig((prev: any) => ({
          ...prev,
          LANDING_BACKGROUND_URL: event.data.SET_LANDING_BACKGROUND_URL,
          usesTestingConfig: true,
        }));
      }

      if (event.data.type === 'SET_CATEGORIES_GRID' && event.data) {
        setTestingConfig((prev: any) => ({
          ...prev,
          MENU_CATEGORIES_GRID: event.data.SET_CATEGORIES_GRID,
          usesTestingConfig: true,
        }));
      }

      if (event.data.type === 'SET_VENUE_IMAGE' && event.data) {
        setTestingVenueImages((prev: any) => ({
          ...prev,
          [event.data.venueId]: event.data.imageUrl,
        }));
      }
    };

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  /**
   * If we have either a cloud config ID or a pathname brand, fetch the
   * cloud config and update the configuration.
   */
  const hasCloudConfig = Config.CLOUD_CONFIG_ID || slug;
  const cloudQuery = useQuery<GetCloudConfig>(GET_CLOUD_CONFIG, {
    variables: {
      id: Config.CLOUD_CONFIG_ID,
      slug,
    },
    skip: !hasCloudConfig,
  });
  const cloudConfig = cloudQuery.data?.orderingCloudConfiguration;

  useEffect(() => {
    if (!cloudConfig) {
      return;
    }

    datadogLogs.addLoggerGlobalContext('company', Config.DISPLAY_NAME.toLowerCase());
  }, [cloudConfig]);

  // Merge the cloud config with the local config.
  const configuration = useMemo(() => {
    if (!cloudConfig) {
      return Config;
    }

    const merged = {
      ...Config,
      ...mapCloudConfigToLocalConfig(cloudConfig),
      ...testingConfig, // Apply any testing config overrides
      testingVenueImages,
    };

    return merged;
  }, [cloudConfig, testingConfig, testingVenueImages]);

  if (hasCloudConfig && cloudConfig === null) {
    console.error(cloudQuery.error);

    return <Error>This domain has not been set up</Error>;
  }

  // TODO: Show a loading indicator while the cloud config is loading.
  if (hasCloudConfig && !cloudConfig) {
    return null;
  }

  return <Context.Provider value={configuration}>{children}</Context.Provider>;
};

export const useConfig = () => React.useContext(Context);

export default ConfigProvider;
